import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    BarChart3, Users, Sparkles, Trophy, ShoppingBag, MapPin,
    BookOpen, TrendingUp, Globe, ShieldAlert, Download, Inbox, Lock, MailX, Mail,
    LineChart as LineChartIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import TrafficChart from "@/components/TrafficChart";

const WINDOWS = [
    { days: 7, key: 7 },
    { days: 30, key: 30 },
    { days: 90, key: 90 },
    { days: 0, key: "all" },  // 0 → all-time
];

export default function AdminInsights() {
    const { t } = useTranslation();
    const { user, loading } = useAuth();
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [pipeline, setPipeline] = useState(null);
    const [emailMetrics, setEmailMetrics] = useState(null);
    const [traffic, setTraffic] = useState(null);
    const [trafficSources, setTrafficSources] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (loading) return;
        if (!user || typeof user !== "object") return;
        let alive = true;
        (async () => {
            setData(null);
            setError(null);
            try {
                const [insights, pipelineRes, metricsRes, trafficRes, sourcesRes] = await Promise.all([
                    api.get("/admin/insights", { params: { days } }),
                    api.get("/admin/pipeline").catch(() => ({ data: null })),
                    api.get("/admin/email-metrics", { params: { days } }).catch(() => ({ data: null })),
                    api.get("/admin/traffic", { params: { days: days || 30 } }).catch(() => ({ data: null })),
                    api.get("/admin/traffic/sources", { params: { days: days || 30 } }).catch(() => ({ data: null })),
                ]);
                if (alive) {
                    setData(insights.data);
                    setPipeline(pipelineRes.data);
                    setEmailMetrics(metricsRes.data);
                    setTraffic(trafficRes.data);
                    setTrafficSources(sourcesRes.data);
                }
            } catch (e) {
                const msg = formatApiError(e.response?.data?.detail) || "Error";
                if (alive) setError(msg);
                if (e.response?.status !== 403) toast.error(msg);
            }
        })();
        return () => { alive = false; };
    }, [days, user, loading]);

    if (loading) return null;
    if (!user || typeof user !== "object") {
        return (
            <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center px-6 text-center">
                <ShieldAlert size={32} strokeWidth={1} className="text-[var(--accent)] mb-4" />
                <p className="font-display text-2xl text-[var(--ink)] mb-3"
                   style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}>
                    {t("admin.login_required")}
                </p>
                <Link to="/acceder" className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-6 py-2 text-sm uppercase tracking-widest">
                    {t("nav.login")}
                </Link>
            </main>
        );
    }
    if (error) {
        return (
            <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center px-6 text-center">
                <ShieldAlert size={32} strokeWidth={1} className="text-[var(--accent)] mb-4" />
                <p className="font-display text-2xl text-[var(--ink)] mb-3"
                   style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}>
                    {error}
                </p>
                <Link to="/" className="text-sm text-[var(--ink-muted)] hover:text-[var(--accent)] transition-colors uppercase tracking-widest mt-4">
                    ← {t("nav.home", "Home")}
                </Link>
            </main>
        );
    }
    if (!data) {
        return <main className="min-h-[calc(100vh-73px)] max-w-7xl mx-auto px-6 py-24"><p className="font-display text-2xl italic text-[var(--ink)] animate-slow-pulse">…</p></main>;
    }

    const csvDownload = async (type) => {
        try {
            const res = await api.get("/admin/export", { params: { type }, responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `recomendalibros-${type}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(t("admin_crud.downloaded"));
        } catch {
            toast.error(t("admin_crud.csv_failed"));
        }
    };

    const CsvButton = ({ type, testId }) => (
        <button
            type="button"
            data-testid={testId}
            onClick={() => csvDownload(type)}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[var(--ink-muted)] hover:text-[var(--accent)] border border-[var(--border-strong)] rounded-full px-3 py-1 transition-colors"
        >
            <Download size={11} strokeWidth={1.5} />
            {type.replace("_", " ")}
        </button>
    );

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
            <div className="border-b border-[var(--border)] pb-8 mb-12 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">{t("admin.eyebrow")}</p>
                    <h1 className="font-display text-5xl md:text-6xl text-[var(--ink)] tracking-tight"
                        style={{ fontVariationSettings: '"wght" 700, "wdth" 80, "opsz" 144' }}>
                        {t("admin.title")}
                    </h1>
                </div>
                <div className="flex items-center gap-3">
                    <Link
                        to="/admin/manage"
                        data-testid="admin-crud-nav"
                        className="hidden sm:inline-flex items-center text-xs uppercase tracking-widest text-[var(--accent)] hover:opacity-80 border border-[var(--accent)] rounded-full px-4 py-1.5 transition-opacity"
                    >
                        {t("admin_crud.manage_link")}
                    </Link>
                    <div className="flex items-center gap-1 text-xs uppercase tracking-widest">
                        {WINDOWS.map((w) => (
                        <button
                            key={w.key}
                            data-testid={`admin-window-${w.key}`}
                            onClick={() => setDays(w.days)}
                            className={`px-4 py-1.5 rounded-full transition-colors ${
                                days === w.days
                                    ? "bg-[var(--ink)] text-[var(--bg)]"
                                    : "border border-[var(--border-strong)] text-[var(--ink-muted)] hover:text-[var(--ink)]"
                            }`}
                        >
                            {w.key === "all" ? t("admin.all_time") : t("admin.last_n_days", { n: w.days })}
                        </button>
                    ))}
                    </div>
                </div>
            </div>

            {/* CSV export toolbar — download any dataset */}
            <section
                data-testid="admin-csv-toolbar"
                className="flex flex-wrap items-center gap-2 mb-10 -mt-4"
            >
                <span className="text-[10px] uppercase tracking-widest text-[var(--ink-dim)] mr-2">
                    <Download size={11} strokeWidth={1.5} className="inline mr-1" />
                    CSV:
                </span>
                <CsvButton type="clicks" testId="csv-btn-clicks" />
                <CsvButton type="top_books" testId="csv-btn-top-books" />
                <CsvButton type="users" testId="csv-btn-users" />
                <CsvButton type="listings" testId="csv-btn-listings" />
                <CsvButton type="sales" testId="csv-btn-sales" />
                <CsvButton type="clubs" testId="csv-btn-clubs" />
                <CsvButton type="club_interest" testId="csv-btn-club-interest" />
                <CsvButton type="contact_messages" testId="csv-btn-contacts" />
            </section>

            {/* Traffic chart — top-of-dashboard at-a-glance metrics */}
            {traffic && <TrafficChart traffic={traffic} sources={trafficSources} />}

            {/* Pipeline panel — combined inbox/leads view */}
            {pipeline && (
                <section
                    data-testid="admin-pipeline"
                    className="border border-[var(--border)] bg-[var(--bg-elevated)] rounded-md p-6 mb-12"
                >
                    <h2
                        className="font-display text-2xl text-[var(--ink)] mb-6 inline-flex items-center gap-3"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 48' }}
                    >
                        <Inbox size={18} strokeWidth={1.5} className="text-[var(--accent)]" />
                        {t("admin_pipeline.title")}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <PipelineColumn
                            icon={Inbox}
                            label={t("admin_pipeline.contacts")}
                            count={pipeline.counts.contact_new}
                            sub={t("admin_pipeline.contacts_sub", { total: pipeline.counts.contact_total })}
                            items={pipeline.contact_preview}
                            renderItem={(it) => (
                                <>
                                    <span className="font-medium text-[var(--ink)]">{it.subject}</span>{" "}
                                    <span className="text-[var(--ink-dim)]">— {it.email}</span>
                                </>
                            )}
                            ctaLabel={t("admin_pipeline.see_all")}
                            ctaTo="/admin/manage"
                            testId="pipeline-contacts"
                        />
                        <PipelineColumn
                            icon={Lock}
                            label={t("admin_pipeline.club_interest")}
                            count={pipeline.counts.club_interest_total}
                            sub={t("admin_pipeline.club_interest_sub")}
                            items={pipeline.club_interest_preview}
                            renderItem={(it) => (
                                <>
                                    <span className="font-medium text-[var(--ink)]">{it.name}</span>{" "}
                                    <span className="text-[var(--ink-dim)]">— {it.email}</span>
                                </>
                            )}
                            ctaLabel={t("admin_pipeline.see_all")}
                            ctaTo="/admin/manage"
                            testId="pipeline-club-interest"
                        />
                        <PipelineColumn
                            icon={MailX}
                            label={t("admin_pipeline.optouts")}
                            count={pipeline.counts.recent_optouts_30d}
                            sub={t("admin_pipeline.optouts_sub", { digests: pipeline.counts.digest_log_total })}
                            items={pipeline.recent_optouts_preview}
                            renderItem={(it) => (
                                <>
                                    <span className="font-medium text-[var(--ink)]">{it.name || it.email}</span>{" "}
                                    <span className="text-[var(--ink-dim)]">— {(it.email_recs_optin_changed_at || "").slice(0, 10)}</span>
                                </>
                            )}
                            ctaLabel={t("admin_pipeline.see_all")}
                            ctaTo="/admin/manage"
                            testId="pipeline-optouts"
                        />
                    </div>
                </section>
            )}

            {/* Email delivery metrics */}
            {emailMetrics && emailMetrics.counts && (
                <section
                    data-testid="admin-email-metrics"
                    className="border border-[var(--border)] bg-[var(--bg-elevated)] rounded-md p-6 mb-12"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                        <h2
                            className="font-display text-2xl text-[var(--ink)] inline-flex items-center gap-3"
                            style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 48' }}
                        >
                            <Mail size={18} strokeWidth={1.5} className="text-[var(--accent)]" />
                            {t("admin_email_metrics.title")}
                        </h2>
                        <p className="text-xs text-[var(--ink-dim)]">
                            {t("admin_email_metrics.window", { n: emailMetrics.window_days })}
                        </p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                        <EmailStat label={t("admin_email_metrics.sent")} value={emailMetrics.counts.sent} testId="email-stat-sent" />
                        <EmailStat
                            label={t("admin_email_metrics.opened")}
                            value={emailMetrics.counts.opened}
                            rate={emailMetrics.rates_percent.opened}
                            testId="email-stat-opened"
                        />
                        <EmailStat
                            label={t("admin_email_metrics.clicked")}
                            value={emailMetrics.counts.clicked}
                            rate={emailMetrics.rates_percent.clicked}
                            testId="email-stat-clicked"
                        />
                        <EmailStat
                            label={t("admin_email_metrics.bounced")}
                            value={emailMetrics.counts.bounced}
                            testId="email-stat-bounced"
                            muted
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-5 border-t border-[var(--border)]">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-2">
                                {t("admin_email_metrics.clicks_by_store")}
                            </p>
                            {Object.keys(emailMetrics.clicks_by_store || {}).length === 0 ? (
                                <p className="text-xs text-[var(--ink-dim)] italic">—</p>
                            ) : (
                                <ul className="space-y-1.5 text-sm">
                                    {Object.entries(emailMetrics.clicks_by_store).map(([store, count]) => (
                                        <li key={store} className="flex justify-between text-[var(--ink-muted)]">
                                            <span className="capitalize">{store}</span>
                                            <span className="text-[var(--ink)] font-mono-num">{count}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-2">
                                {t("admin_email_metrics.feedback")}
                            </p>
                            <ul className="space-y-1.5 text-sm">
                                <li className="flex justify-between text-[var(--ink-muted)]">
                                    <span>👍 {t("admin_email_metrics.likes")}</span>
                                    <span className="text-[var(--ink)] font-mono-num">{emailMetrics.counts.likes}</span>
                                </li>
                                <li className="flex justify-between text-[var(--ink-muted)]">
                                    <span>📧 {t("admin_email_metrics.email_likes")}</span>
                                    <span className="text-[var(--ink)] font-mono-num" data-testid="email-stat-email-likes">{emailMetrics.counts.email_likes ?? 0}</span>
                                </li>
                                <li className="flex justify-between text-[var(--ink-muted)]">
                                    <span>👎 {t("admin_email_metrics.dislikes")}</span>
                                    <span className="text-[var(--ink)] font-mono-num">{emailMetrics.counts.dislikes}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </section>
            )}

            {/* High-level KPIs */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16" data-testid="admin-kpis">
                <Kpi icon={Users} label={t("admin.users_total")} value={data.users.total} sub={t("admin.users_new", { n: data.users.new })} />
                <Kpi icon={Sparkles} label={t("admin.recs_total")} value={data.recommendations.total} sub={t("admin.recs_recent", { n: data.recommendations.recent })} />
                <Kpi icon={TrendingUp} label={t("admin.affiliate_clicks")} value={data.clicks.total_recent} sub={t("admin.ctr", { n: data.recommendations.ctr_per_rec })} />
                <Kpi icon={ShoppingBag} label={t("admin.listings_active")} value={data.marketplace.listings_active} sub={t("admin.listings_total", { n: data.marketplace.listings_total })} />
            </section>

            {/* Affiliate clicks by store */}
            <Block icon={BarChart3} title={t("admin.clicks_by_store")}>
                {data.clicks.by_store.length === 0 ? (
                    <p className="text-sm text-[var(--ink-dim)] italic">{t("admin.no_clicks")}</p>
                ) : (
                    <ul className="space-y-3" data-testid="admin-clicks-by-store">
                        {data.clicks.by_store.map((row) => {
                            const max = Math.max(...data.clicks.by_store.map((r) => r.clicks));
                            const pct = max > 0 ? Math.round((row.clicks / max) * 100) : 0;
                            return (
                                <li key={row.store} className="flex items-center gap-4">
                                    <span className="text-sm text-[var(--ink)] w-32 capitalize">{row.store.replace("_", " ")}</span>
                                    <div className="flex-1 h-2 bg-[var(--bg-strong)] rounded-full overflow-hidden">
                                        <div className="h-full bg-[var(--accent)]" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="font-mono-num text-sm text-[var(--ink)] w-16 text-right">{row.clicks}</span>
                                </li>
                            );
                        })}
                    </ul>
                )}
                <p className="text-xs text-[var(--ink-dim)] mt-4 inline-flex items-center gap-1.5">
                    <MapPin size={11} strokeWidth={1.5} />
                    {t("admin.local_clicks", { n: data.clicks.local_bookshop_clicks })}
                </p>
            </Block>

            {/* Top books */}
            <Block icon={BookOpen} title={t("admin.top_books")}>
                {data.top_books.length === 0 ? (
                    <p className="text-sm text-[var(--ink-dim)] italic">{t("admin.no_top_books")}</p>
                ) : (
                    <ol className="space-y-2" data-testid="admin-top-books">
                        {data.top_books.map((b, i) => (
                            <li key={`${b.title}-${i}`} className="flex items-center gap-4 border-b border-[var(--border)] py-2.5">
                                <span className="font-mono-num text-xs text-[var(--accent)] w-6 text-right">{(i + 1).toString().padStart(2, "0")}</span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-[var(--ink)] truncate">{b.title}</p>
                                    {b.author && <p className="text-xs text-[var(--ink-dim)] truncate">{b.author}</p>}
                                </div>
                                <span className="text-xs text-[var(--ink-muted)] hidden sm:inline">
                                    {b.stores.join(" · ")}
                                </span>
                                <span className="font-mono-num text-sm text-[var(--ink)] w-16 text-right">{b.clicks}</span>
                            </li>
                        ))}
                    </ol>
                )}
            </Block>

            {/* Marketplace */}
            <Block icon={ShoppingBag} title={t("admin.marketplace_summary")}>
                {data.marketplace.sales_summary.length === 0 ? (
                    <p className="text-sm text-[var(--ink-dim)] italic">{t("admin.no_sales")}</p>
                ) : (
                    <div className="space-y-6" data-testid="admin-marketplace">
                        {data.marketplace.sales_summary.map((s) => (
                            <article key={s.currency} className="border border-[var(--border)] p-6">
                                <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--accent)] mb-4">
                                    {s.currency.toUpperCase()} · {t("admin.sales_count", { n: s.sales_count })}
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                                    <Metric label={t("admin.gmv_sold")} value={s.gmv_sold} currency={s.currency} primary />
                                    <Metric label={t("admin.commission_earned")} value={s.commission_earned} currency={s.currency} accent />
                                    <Metric label={t("admin.stripe_fees")} value={s.stripe_fees_estimate} currency={s.currency} muted />
                                    <Metric label={t("admin.net_earned")} value={s.net_earned} currency={s.currency} accent />
                                    <Metric label={t("admin.avg_book_price")} value={s.avg_book_price} currency={s.currency} muted />
                                </div>
                            </article>
                        ))}
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-[var(--border)]">
                    <p className="text-xs text-[var(--ink-dim)] inline-flex items-center gap-1.5">
                        <ShoppingBag size={11} strokeWidth={1.5} />
                        {t("admin.failed_sales", { n: data.marketplace.failed_or_pending_count })}
                    </p>
                    {Object.entries(data.marketplace.gmv_listed_by_currency || {}).map(([cur, info]) => (
                        <p key={cur} className="text-xs text-[var(--ink-dim)] inline-flex items-center gap-1.5">
                            <BarChart3 size={11} strokeWidth={1.5} />
                            {t("admin.gmv_listed", {
                                amount: info.gmv_listed.toFixed(2),
                                currency: cur.toUpperCase(),
                                n: info.count,
                            })}
                        </p>
                    ))}
                </div>
                <p className="text-[10px] text-[var(--ink-dim)] italic mt-4">
                    {t("admin.stripe_assumption", { rule: data.marketplace.stripe_fee_assumption })}
                </p>
            </Block>

            {/* Clubs */}
            <Block icon={Trophy} title={t("admin.clubs_top")}>
                {data.clubs.leaderboard.length === 0 ? (
                    <p className="text-sm text-[var(--ink-dim)] italic">{t("admin.no_clubs")}</p>
                ) : (
                    <ol className="space-y-2" data-testid="admin-clubs">
                        {data.clubs.leaderboard.map((c, i) => (
                            <li key={c.id} className="flex items-center gap-4 border-b border-[var(--border)] py-2.5">
                                <span className="font-mono-num text-xs text-[var(--accent)] w-6 text-right">{(i + 1).toString().padStart(2, "0")}</span>
                                <Link to={`/clubs/${c.slug}`} className="text-sm text-[var(--ink)] truncate flex-1 hover:text-[var(--accent)] transition-colors">
                                    {c.name}
                                </Link>
                                <span className="text-[10px] uppercase tracking-widest text-[var(--ink-dim)]">
                                    {c.kind} · {c.language?.toUpperCase()}
                                </span>
                                <span className="text-xs text-[var(--ink-muted)] inline-flex items-center gap-1">
                                    <Users size={11} strokeWidth={1.5} /> {c.member_count}
                                </span>
                                <span className="text-xs text-[var(--ink-muted)] inline-flex items-center gap-1">
                                    <BookOpen size={11} strokeWidth={1.5} /> {c.cycle_count}
                                </span>
                            </li>
                        ))}
                    </ol>
                )}
                <p className="text-xs text-[var(--ink-dim)] mt-4 inline-flex items-center gap-1.5">
                    <Globe size={11} strokeWidth={1.5} />
                    {t("admin.clubs_total", { n: data.clubs.total })}
                </p>
            </Block>

            <p className="text-xs text-[var(--ink-dim)] italic mt-12 border-t border-[var(--border)] pt-6">
                {t("admin.footer", { date: new Date(data.generated_at).toLocaleString() })}
            </p>
        </main>
    );
}

function Kpi({ icon: Icon, label, value, sub }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-2 inline-flex items-center gap-1.5">
                <Icon size={11} strokeWidth={1.5} /> {label}
            </p>
            <p className="font-display text-5xl text-[var(--ink)] font-mono-num leading-none mb-1.5"
               style={{ fontVariationSettings: '"wght" 700, "wdth" 85, "opsz" 96' }}>
                {value}
            </p>
            {sub && <p className="text-xs text-[var(--ink-dim)]">{sub}</p>}
        </div>
    );
}

function Block({ icon: Icon, title, children }) {
    return (
        <section className="mb-16">
            <h2 className="font-display text-2xl md:text-3xl text-[var(--ink)] mb-6 inline-flex items-center gap-3"
                style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 48' }}>
                <Icon size={18} strokeWidth={1.5} className="text-[var(--accent)]" />
                {title}
            </h2>
            {children}
        </section>
    );
}

function Metric({ label, value, currency, primary, accent, muted }) {
    const colour = primary
        ? "text-[var(--ink)]"
        : accent
        ? "text-[var(--accent)]"
        : muted
        ? "text-[var(--ink-muted)]"
        : "text-[var(--ink)]";
    return (
        <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-1">{label}</p>
            <p className={`font-display text-xl ${colour} font-mono-num`}
               style={{ fontVariationSettings: '"wght" 600, "wdth" 92, "opsz" 24' }}>
                {(value ?? 0).toFixed(2)}
                <span className="text-xs ml-1 text-[var(--ink-dim)]">{(currency || "").toUpperCase()}</span>
            </p>
        </div>
    );
}

function EmailStat({ label, value, rate, testId, muted }) {
    return (
        <div data-testid={testId}>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-1">{label}</p>
            <p className={`font-display text-2xl font-mono-num ${muted ? "text-[var(--ink-muted)]" : "text-[var(--ink)]"}`}
               style={{ fontVariationSettings: '"wght" 700, "wdth" 90, "opsz" 48' }}>
                {value ?? 0}
            </p>
            {rate != null && (
                <p className="text-[10px] uppercase tracking-widest text-[var(--accent)] mt-0.5">
                    {rate}%
                </p>
            )}
        </div>
    );
}

function PipelineColumn({ icon: Icon, label, count, sub, items, renderItem, ctaLabel, ctaTo, testId }) {
    return (
        <div data-testid={testId} className="flex flex-col">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-2 inline-flex items-center gap-1.5">
                <Icon size={11} strokeWidth={1.5} /> {label}
            </p>
            <p className="font-display text-4xl text-[var(--ink)] font-mono-num leading-none mb-1.5"
               style={{ fontVariationSettings: '"wght" 700, "wdth" 85, "opsz" 96' }}>
                {count ?? 0}
            </p>
            <p className="text-xs text-[var(--ink-dim)] mb-4">{sub}</p>
            <ul className="space-y-2 text-xs flex-1 mb-3">
                {(items || []).slice(0, 4).map((it, i) => (
                    <li key={it.id || it.email || i} className="text-[var(--ink-muted)] truncate">
                        {renderItem(it)}
                    </li>
                ))}
                {(!items || items.length === 0) && (
                    <li className="text-[var(--ink-dim)] italic">—</li>
                )}
            </ul>
            <Link
                to={ctaTo}
                className="text-[10px] uppercase tracking-widest text-[var(--accent)] hover:opacity-80 self-start"
            >
                {ctaLabel} →
            </Link>
        </div>
    );
}
